import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { DataService } from '../../Services/dataService';
import { Modal, Button, Table, Form, Row, Col } from 'react-bootstrap-v5';

const DISCOUNT_TYPES = [
    { value: 'Volume Discount', condition: 'CBM', unit: 'CBM', range: true },
    { value: 'Weight Discount', condition: 'Weight', unit: 'kg', range: true },
    { value: 'Product Specific Discount', condition: 'Product' },
    { value: 'Promo Code Discount', condition: 'PromoCode' },
    { value: 'HS Code Discount', condition: 'HSCode' },
    { value: 'Countries Discount', condition: 'Country' }
];

const typeSetup = (type, conditionType) => {
    const named = DISCOUNT_TYPES.find((item) => item.value === type);
    if (named) return named;
    const fromCondition = DISCOUNT_TYPES.find((item) => item.condition === conditionType);
    return fromCondition || DISCOUNT_TYPES[0];
};

const emptyForm = {
    type: 'Volume Discount',
    mode: 'Courier',
    condition_type: 'CBM',
    condition_value: '',
    condition_min: '',
    condition_max: '',
    hs_codes: [],
    countries: [],
    product_names: [],
    discount_type: 'Percentage',
    discount_value: '',
    valid_from: '',
    valid_to: '',
    is_active: true
};

const dateInput = (value) => (value ? String(value).split('T')[0] : '');

const listFromRule = (list, fallback) => {
    if (Array.isArray(list) && list.length) return list;
    return String(fallback || '').split(',').map((item) => item.trim()).filter(Boolean);
};

const conditionSummary = (rule) => {
    if (rule.condition_type === 'Weight' || rule.condition_type === 'CBM') {
        const unit = rule.condition_type === 'Weight' ? 'kg' : 'CBM';
        const hasMin = rule.condition_min !== undefined && rule.condition_min !== null && rule.condition_min !== '';
        const hasMax = rule.condition_max !== undefined && rule.condition_max !== null && rule.condition_max !== '';
        if (hasMin && hasMax) return `${rule.condition_min}–${rule.condition_max} ${unit}`;
        if (hasMin) return `${rule.condition_min} ${unit} and above`;
        if (hasMax) return `Up to ${rule.condition_max} ${unit}`;
        return rule.condition_value ? `${rule.condition_value} ${unit} and above` : '-';
    }
    if (rule.condition_type === 'HSCode' || rule.type === 'HS Code Discount') return listFromRule(rule.hs_codes, rule.condition_value).join(', ') || '-';
    if (rule.condition_type === 'Country' || rule.type === 'Countries Discount' || rule.type === 'Country Specific Discount') return listFromRule(rule.countries, rule.condition_value).join(', ') || '-';
    if (rule.condition_type === 'Product' || rule.type === 'Product Specific Discount') return listFromRule(rule.product_names, rule.condition_value).join(', ') || '-';
    return rule.condition_value || '-';
};

const validitySummary = (rule) => {
    const from = rule.valid_from ? new Date(rule.valid_from).toLocaleDateString() : '';
    const to = rule.valid_to ? new Date(rule.valid_to).toLocaleDateString() : '';
    if (from && to) return `${from} – ${to}`;
    if (from) return `From ${from}, no expiry`;
    if (to) return `Until ${to}`;
    return 'No expiry';
};

const DiscountSettings = () => {
    const [rules, setRules] = useState([]);
    const [countries, setCountries] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [productInput, setProductInput] = useState('');
    const [countrySearch, setCountrySearch] = useState('');
    const [hsSearch, setHsSearch] = useState('');
    const [promoSearch, setPromoSearch] = useState('');
    const [hsCodeOptions, setHsCodeOptions] = useState([]);
    const [promoOptions, setPromoOptions] = useState([]);

    useEffect(() => {
        fetchRules();
        fetchCountries();
        fetchHsCodes();
        fetchPromos();
    }, []);

    const fetchCountries = async () => {
        try {
            const res = await DataService.get('/admin/get_country');
            const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
            setCountries(list);
        } catch (error) {
            console.error('Failed to load countries', error);
        }
    };

    const fetchHsCodes = async () => {
        try {
            const res = await DataService.get('/admin/hscode_duty_taxes');
            const list = Array.isArray(res?.data) ? res.data : [];
            const codes = new Set();
            list.forEach((country) => {
                (country.data || []).forEach((row) => {
                    const code = String(row?.hs_code || '').trim();
                    if (code) codes.add(code);
                });
            });
            setHsCodeOptions(Array.from(codes).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
        } catch (error) {
            console.error('Failed to load HS codes', error);
        }
    };

    const fetchPromos = async () => {
        try {
            const res = await DataService.get('/admin/get_promo', { page: 1, limit: 1000, search: '' });
            const list = Array.isArray(res?.data) ? res.data : [];
            const seen = new Set();
            const options = [];
            list.forEach((item) => {
                const code = String(item?.promo_code || '').trim();
                if (!code || seen.has(code.toLowerCase())) return;
                seen.add(code.toLowerCase());
                options.push({
                    code,
                    status: item.promo_status || '',
                });
            });
            options.sort((a, b) => a.code.localeCompare(b.code));
            setPromoOptions(options);
        } catch (error) {
            console.error('Failed to load promo codes', error);
        }
    };

    const fetchRules = async () => {
        setLoading(true);
        try {
            const res = await DataService.get('/admin/discounts');
            if (res.success) setRules(res.data || []);
        } catch (error) {
            toast.error('Failed to load discount rules');
        } finally {
            setLoading(false);
        }
    };

    const handleShow = (rule = null) => {
        if (rule) {
            setEditingRule(rule);
            const setup = typeSetup(rule.type, rule.condition_type);
            const isRange = setup.range;
            setFormData({
                ...emptyForm,
                ...rule,
                type: setup.value,
                condition_type: setup.condition,
                discount_type: 'Percentage',
                condition_min: isRange && rule.condition_min != null ? rule.condition_min : (isRange && rule.condition_value && rule.condition_max == null ? rule.condition_value : ''),
                condition_max: isRange && rule.condition_max != null ? rule.condition_max : '',
                hs_codes: listFromRule(rule.hs_codes, setup.condition === 'HSCode' ? rule.condition_value : ''),
                countries: listFromRule(rule.countries, setup.condition === 'Country' ? rule.condition_value : ''),
                product_names: listFromRule(rule.product_names, setup.condition === 'Product' ? rule.condition_value : ''),
                valid_from: dateInput(rule.valid_from),
                valid_to: dateInput(rule.valid_to)
            });
        } else {
            setEditingRule(null);
            setFormData(emptyForm);
        }
        setProductInput('');
        setCountrySearch('');
        setHsSearch('');
        setPromoSearch('');
        setShowModal(true);
    };

    const handleClose = () => {
        setShowModal(false);
        setEditingRule(null);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'type') {
            const setup = typeSetup(value);
            setFormData({
                ...formData,
                type: setup.value,
                condition_type: setup.condition,
                discount_type: 'Percentage',
                condition_value: '',
                condition_min: '',
                condition_max: '',
                hs_codes: [],
                countries: [],
                product_names: []
            });
            setProductInput('');
            setHsSearch('');
            setPromoSearch('');
            return;
        }
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const addChip = (field, input, setInput) => {
        const value = input.trim();
        if (!value) return;
        const current = formData[field] || [];
        if (current.some((item) => item.toLowerCase() === value.toLowerCase())) {
            setInput('');
            return;
        }
        setFormData({ ...formData, [field]: [...current, value] });
        setInput('');
    };

    const addProduct = () => addChip('product_names', productInput, setProductInput);

    const toggleCountry = (name) => {
        const selected = formData.countries.includes(name)
            ? formData.countries.filter((item) => item !== name)
            : [...formData.countries, name];
        setFormData({ ...formData, countries: selected });
    };

    const toggleHsCode = (code) => {
        const selected = formData.hs_codes.some((item) => item.toLowerCase() === code.toLowerCase())
            ? formData.hs_codes.filter((item) => item.toLowerCase() !== code.toLowerCase())
            : [...formData.hs_codes, code];
        setFormData({ ...formData, hs_codes: selected });
    };

    const handleSave = async () => {
        if (formData.discount_value === '' || formData.discount_value === null) {
            toast.error('Enter the discount value.');
            return;
        }
        if (formData.condition_type === 'Product' && formData.product_names.length === 0) {
            toast.error('Add at least one product name.');
            return;
        }
        if (formData.condition_type === 'Weight' || formData.condition_type === 'CBM') {
            const min = formData.condition_min === '' ? null : Number(formData.condition_min);
            const max = formData.condition_max === '' ? null : Number(formData.condition_max);
            if (min == null && max == null) {
                toast.error('Enter a lower amount, an upper amount, or both.');
                return;
            }
            if (min != null && max != null && min > max) {
                toast.error('The lower amount cannot be higher than the upper amount.');
                return;
            }
        }
        if (formData.condition_type === 'HSCode' && formData.hs_codes.length === 0) {
            toast.error('Select at least one HS code.');
            return;
        }
        if (formData.condition_type === 'Country' && formData.countries.length === 0) {
            toast.error('Select at least one country.');
            return;
        }
        if (formData.condition_type === 'PromoCode' && !String(formData.condition_value || '').trim()) {
            toast.error('Select a promo code.');
            return;
        }
        if (formData.valid_from && formData.valid_to && formData.valid_from > formData.valid_to) {
            toast.error('Valid to cannot be before valid from.');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                ...formData,
                valid_from: formData.valid_from || null,
                valid_to: formData.valid_to || null
            };
            const res = editingRule
                ? await DataService.put(`/admin/discounts/${editingRule._id}`, payload)
                : await DataService.post('/admin/discounts', payload);
            if (res.success) toast.success(editingRule ? 'Rule updated' : 'Rule created');
            handleClose();
            fetchRules();
        } catch (error) {
            toast.error('Error saving rule');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            const res = await DataService.delete(`/admin/discounts/${id}`);
            if (res.success) toast.success('Rule deleted');
            fetchRules();
        } catch (error) {
            toast.error('Error deleting rule');
        }
    };

    const countryOptions = countries.filter((country) => {
        const name = country.country_name || country.name || '';
        return name.toLowerCase().includes(countrySearch.trim().toLowerCase());
    });

    const hsQuery = hsSearch.trim().toLowerCase();
    const knownHsCodes = new Set(hsCodeOptions.map((code) => code.toLowerCase()));
    const extraHsCodes = formData.hs_codes.filter((code) => !knownHsCodes.has(String(code).toLowerCase()));
    const hsOptions = [...extraHsCodes, ...hsCodeOptions].filter((code) => (
        !hsQuery || String(code).toLowerCase().includes(hsQuery)
    ));

    const promoQuery = promoSearch.trim().toLowerCase();
    const savedPromo = String(formData.condition_value || '').trim();
    const promoChoices = promoOptions.some((item) => item.code.toLowerCase() === savedPromo.toLowerCase()) || !savedPromo
        ? promoOptions
        : [{ code: savedPromo, status: '' }, ...promoOptions];
    const filteredPromos = promoChoices.filter((item) => item.code.toLowerCase().includes(promoQuery));

    return (
        <div className="card mt-4">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5>Discount Settings Engine</h5>
                <Button variant="primary" onClick={() => handleShow()}>Add New Discount Rule</Button>
            </div>
            <div className="card-body">
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Mode</th>
                            <th>Match</th>
                            <th>Discount</th>
                            <th>Validity</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map((rule) => (
                            <tr key={rule._id}>
                                <td>{rule.type}</td>
                                <td>{rule.mode}</td>
                                <td>{conditionSummary(rule)}</td>
                                <td>{rule.discount_value}%</td>
                                <td>{validitySummary(rule)}</td>
                                <td>{rule.is_active ? 'Active' : 'Inactive'}</td>
                                <td>
                                    <Button variant="info" size="sm" className="mr-2" onClick={() => handleShow(rule)}>Edit</Button>
                                    <Button variant="danger" size="sm" onClick={() => handleDelete(rule._id)}>Delete</Button>
                                </td>
                            </tr>
                        ))}
                        {!rules.length && (
                            <tr><td colSpan="7" className="text-center text-muted">No discount rules yet.</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <Modal show={showModal} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingRule ? 'Edit Discount Rule' : 'Add Discount Rule'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Discount Type</Form.Label>
                                    <Form.Control
                                        as="select"
                                        className="form-select"
                                        name="type"
                                        value={formData.type}
                                        onChange={handleChange}
                                        style={{
                                            backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e\")",
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition: "right 0.75rem center",
                                            backgroundSize: "16px 12px",
                                            appearance: "none",
                                        }}
                                    >
                                        {DISCOUNT_TYPES.map((item) => (
                                            <option key={item.value} value={item.value}>{item.value}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Mode of Transport</Form.Label>
                                    <Form.Control
                                        as="select"
                                        className="form-select"
                                        name="mode"
                                        value={formData.mode}
                                        onChange={handleChange}
                                        style={{
                                            backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e\")",
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition: "right 0.75rem center",
                                            backgroundSize: "16px 12px",
                                            appearance: "none",
                                        }}
                                    >
                                        <option value="Courier">Courier</option>
                                        <option value="Air">Air</option>
                                        <option value="Sea">Sea</option>
                                        <option value="Land">Land</option>
                                        <option value="All Modes">All Modes</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>
                        {(formData.condition_type === 'Weight' || formData.condition_type === 'CBM') && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>From</Form.Label>
                                        <div className="input-group">
                                            <Form.Control type="number" name="condition_min" value={formData.condition_min} onChange={handleChange} placeholder="Lower amount, optional" />
                                            <span className="input-group-text">{formData.condition_type === 'Weight' ? 'kg' : 'CBM'}</span>
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>To</Form.Label>
                                        <div className="input-group">
                                            <Form.Control type="number" name="condition_max" value={formData.condition_max} onChange={handleChange} placeholder="Upper amount, optional" />
                                            <span className="input-group-text">{formData.condition_type === 'Weight' ? 'kg' : 'CBM'}</span>
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <p className="text-muted small">The shipment matches when the {formData.condition_type === 'Weight' ? 'weight' : 'volume'} is inside this range, including the amounts you enter. Leave From empty for no lower limit. Leave To empty for no upper limit.</p>
                                </Col>
                            </Row>
                        )}

                        {formData.condition_type === 'Product' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Product names</Form.Label>
                                <div className="d-flex" style={{ gap: 8 }}>
                                    <Form.Control
                                        type="text"
                                        value={productInput}
                                        placeholder="Type one product name and add it"
                                        onChange={(e) => setProductInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addProduct();
                                            }
                                        }}
                                    />
                                    <Button variant="outline-primary" type="button" onClick={addProduct}>Add</Button>
                                </div>
                                <div className="d-flex flex-wrap mt-2" style={{ gap: 6 }}>
                                    {formData.product_names.map((name) => (
                                        <span key={name} className="badge bg-light text-dark border d-inline-flex align-items-center" style={{ gap: 6, padding: '6px 8px' }}>
                                            {name}
                                            <button type="button" className="btn btn-sm p-0" onClick={() => setFormData({ ...formData, product_names: formData.product_names.filter((item) => item !== name) })}>×</button>
                                        </span>
                                    ))}
                                </div>
                                <p className="text-muted small mb-0 mt-2">The discount applies when any product on the shipment matches one of these names.</p>
                            </Form.Group>
                        )}

                        {formData.condition_type === 'HSCode' && (
                            <Form.Group className="mb-3">
                                <Form.Label>HS codes</Form.Label>
                                <div className="d-flex flex-wrap mb-2" style={{ gap: 6 }}>
                                    {formData.hs_codes.map((code) => (
                                        <span key={code} className="badge bg-light text-dark border d-inline-flex align-items-center" style={{ gap: 6, padding: '6px 8px' }}>
                                            {code}
                                            <button type="button" className="btn btn-sm p-0" onClick={() => toggleHsCode(code)}>×</button>
                                        </span>
                                    ))}
                                </div>
                                <Form.Control className="mb-2" type="text" value={hsSearch} placeholder="Search HS code" onChange={(e) => setHsSearch(e.target.value)} />
                                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #ced4da', borderRadius: 4, padding: 8 }}>
                                    {hsOptions.map((code) => (
                                        <Form.Check
                                            key={code}
                                            type="checkbox"
                                            label={code}
                                            checked={formData.hs_codes.some((item) => item.toLowerCase() === String(code).toLowerCase())}
                                            onChange={() => toggleHsCode(code)}
                                        />
                                    ))}
                                    {!hsOptions.length && <div className="text-muted small">No HS code found.</div>}
                                </div>
                                <p className="text-muted small mb-0 mt-2">Select every HS code this discount covers. The discount applies when any product on the shipment uses one of these codes.</p>
                            </Form.Group>
                        )}

                        {formData.condition_type === 'Country' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Countries</Form.Label>
                                <div className="d-flex flex-wrap mb-2" style={{ gap: 6 }}>
                                    {formData.countries.map((name) => (
                                        <span key={name} className="badge bg-light text-dark border d-inline-flex align-items-center" style={{ gap: 6, padding: '6px 8px' }}>
                                            {name}
                                            <button type="button" className="btn btn-sm p-0" onClick={() => toggleCountry(name)}>×</button>
                                        </span>
                                    ))}
                                </div>
                                <Form.Control className="mb-2" type="text" value={countrySearch} placeholder="Search country" onChange={(e) => setCountrySearch(e.target.value)} />
                                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #ced4da', borderRadius: 4, padding: 8 }}>
                                    {countryOptions.map((country) => {
                                        const name = country.country_name || country.name;
                                        return (
                                            <Form.Check
                                                key={country._id || name}
                                                type="checkbox"
                                                label={name}
                                                checked={formData.countries.includes(name)}
                                                onChange={() => toggleCountry(name)}
                                            />
                                        );
                                    })}
                                    {!countryOptions.length && <div className="text-muted small">No country found.</div>}
                                </div>
                                <p className="text-muted small mb-0 mt-2">Select every destination country this discount covers.</p>
                            </Form.Group>
                        )}

                        {formData.condition_type === 'PromoCode' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Promo code</Form.Label>
                                {savedPromo && (
                                    <div className="d-flex flex-wrap mb-2" style={{ gap: 6 }}>
                                        <span className="badge bg-light text-dark border d-inline-flex align-items-center" style={{ gap: 6, padding: '6px 8px' }}>
                                            {savedPromo}
                                            <button type="button" className="btn btn-sm p-0" onClick={() => setFormData({ ...formData, condition_value: '' })}>×</button>
                                        </span>
                                    </div>
                                )}
                                <Form.Control className="mb-2" type="text" value={promoSearch} placeholder="Search promo code" onChange={(e) => setPromoSearch(e.target.value)} />
                                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #ced4da', borderRadius: 4, padding: 8 }}>
                                    {filteredPromos.map((item) => (
                                        <Form.Check
                                            key={item.code}
                                            type="radio"
                                            name="discount_promo_code"
                                            label={item.status && item.status !== 'ACTIVE' ? `${item.code} (${item.status})` : item.code}
                                            checked={savedPromo.toLowerCase() === item.code.toLowerCase()}
                                            onChange={() => setFormData({ ...formData, condition_value: item.code })}
                                        />
                                    ))}
                                    {!filteredPromos.length && <div className="text-muted small">No promo code found. Add one in Promo first.</div>}
                                </div>
                                <p className="text-muted small mb-0 mt-2">Select a promo code created in Promo. The discount applies when the customer uses that code.</p>
                            </Form.Group>
                        )}

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Discount value <span className="text-danger">*</span></Form.Label>
                                    <div className="input-group">
                                        <Form.Control type="number" name="discount_value" value={formData.discount_value} onChange={handleChange} />
                                        <span className="input-group-text">%</span>
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Valid from</Form.Label>
                                    <Form.Control type="date" name="valid_from" value={formData.valid_from || ''} onChange={handleChange} />
                                    <Form.Text className="text-muted">First day this discount can apply. Leave empty to allow it from today.</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Valid to</Form.Label>
                                    <Form.Control type="date" name="valid_to" value={formData.valid_to || ''} onChange={handleChange} />
                                    <Form.Text className="text-muted">Last day this discount applies. Leave empty for no expiry.</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mt-2">
                            <Form.Check type="checkbox" label="Is Active" name="is_active" checked={formData.is_active} onChange={handleChange} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} disabled={loading}>{loading ? 'Saving...' : 'Save Rule'}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DiscountSettings;
